import { PaginationState, createColumnHelper } from "@tanstack/react-table";
import Table from "../../../../ui/Table/Table";
import { graphql } from "../../../../__generated__/gql";
import { useQuery } from "urql";
import { useMemo, useState } from "react";
import styles from "./CreditUsage.module.scss";
import FormattedDate from "../../../../ui/FormattedDate/FormattedDate";
import Truncate from "../../../../ui/Truncate/Truncate";

const CreditUsagesQuery = graphql(/* GraphQL */ `
  query creditUsages($paginationOptions: OffsetPaginationOptions) {
    creditUsages(paginationOptions: $paginationOptions) {
      nodes {
        credits
        createdAt
        tenant {
          id
          name
        }
      }
      totalCount
    }
  }
`);

type CreditUsageTableRow = {
  usage: string;
  creditsUsed: number;
  date: string;
  team?: string;
};

const DEFAULT_PAGINATION_STATE: PaginationState = {
  pageIndex: 0,
  pageSize: 25,
};

function CreditUsageTable() {
  const [pagination, setPagination] = useState<PaginationState>(
    DEFAULT_PAGINATION_STATE,
  );

  const [creditUsagesResponse] = useQuery({
    query: CreditUsagesQuery,
    requestPolicy: "cache-and-network",
    variables: {
      paginationOptions: {
        take: pagination.pageSize,
        skip: pagination.pageIndex * pagination.pageSize,
      },
    },
  });

  const columnHelper = createColumnHelper<CreditUsageTableRow>();

  const columns = [
    columnHelper.accessor("usage", {
      header: "Usage",
      cell: ({ cell }) => (
        <span className={styles.usageCell}>{cell.getValue()}</span>
      ),
    }),
    columnHelper.accessor("creditsUsed", {
      header: "Credits used",
      size: 122,
    }),
    columnHelper.accessor("team", {
      header: "Team",
      size: 220,
      cell: ({ cell }) => <Truncate>{cell.getValue() ?? "-"}</Truncate>,
    }),
    columnHelper.accessor("date", {
      header: "Date",
      cell: ({ cell }) => <FormattedDate date={cell.getValue()} />,
      size: 140,
    }),
  ];

  const data: CreditUsageTableRow[] = useMemo(
    () =>
      creditUsagesResponse.data?.creditUsages.nodes.map((creditUsage) => {
        let usage;

        if (creditUsage.credits > 0) {
          usage = `${creditUsage.credits} ${
            creditUsage.credits === 1 ? "Article" : "Articles"
          } Generated`;
        } else {
          usage = `${Math.abs(creditUsage.credits)} ${
            creditUsage.credits === -1 ? "Article" : "Articles"
          } Refunded`;
        }

        return {
          usage,
          creditsUsed: creditUsage.credits,
          date: creditUsage.createdAt,
          team: creditUsage.tenant?.name,
        };
      }) ?? [],
    [creditUsagesResponse.data?.creditUsages.nodes],
  );

  return (
    <Table
      className={styles.table}
      Header={
        <div className={styles.header}>
          <h2 className={styles.usageTableTitle}>Article credit usage</h2>
          <p className={styles.usageTableDescription}>
            View your generation history
          </p>
        </div>
      }
      columns={columns}
      data={data}
      loading={creditUsagesResponse.fetching}
      pagination={pagination}
      setPagination={setPagination}
      totalCount={creditUsagesResponse.data?.creditUsages.totalCount}
      resourceName={{ plural: "credit usage", singular: "credit usage" }}
    />
  );
}

export default CreditUsageTable;
